import { z } from "zod";
import { DeltaSchema } from "./delta.js";
import { AttachmentSchema, AttachmentKindSchema } from "./attachment.js";
import { GameActionEventSchema, GameEventSchema, GameMatchUpdatedEventSchema, JoinMatchEventSchema, } from "./games.js";
import { GroupActivityEventSchema, GroupTurnArchivedEventSchema, JoinGroupEventSchema, } from "./groups.js";
import { PointsAwardedEventSchema } from "./points.js";
/**
 * Protocol-level hard cap on attachments per turn. The server may enforce a
 * lower value via MAX_ATTACHMENTS_PER_TURN, never a higher one.
 */
export const MAX_ATTACHMENTS_PER_TURN = 10;
// ---------------------------------------------------------------------------
// WebSocket event schemas — discriminated union on "type"
// ---------------------------------------------------------------------------
export const HelloEventSchema = z.object({
    type: z.literal("hello"),
    userId: z.string().min(1),
    ts: z.number(),
});
export const DeltaEventSchema = z.object({
    type: z.literal("delta"),
    conversationId: z.string().min(1),
    delta: DeltaSchema,
    /** Ajouté par le serveur à la diffusion — indispensable en groupe (G3a)
     *  pour router le delta vers le bon mini-miroir. Absent client→serveur. */
    authorId: z.string().optional(),
});
export const PingEventSchema = z.object({
    type: z.literal("ping"),
    ts: z.number(),
});
export const PongEventSchema = z.object({
    type: z.literal("pong"),
    ts: z.number(),
});
export const EndTurnEventSchema = z.object({
    type: z.literal("end_turn"),
    conversationId: z.string().min(1),
    ts: z.number(),
    // Optional: sending media is the one explicit send action in the product —
    // the text buffer still archives on Enter / 10s of inactivity as usual.
    // Optional keeps older clients wire-compatible (AGENTS.md §10).
    attachmentIds: z.array(z.string().uuid()).max(MAX_ATTACHMENTS_PER_TURN).optional(),
});
export const JoinSessionEventSchema = z.object({
    type: z.literal("join_session"),
    sessionId: z.string().min(1),
});
// Multiplexage (spec docs/WS_MULTIPLEX_SPEC.md) : quitter un canal sans
// fermer le socket. Sans id : un socket n'a qu'une session/un groupe/une
// partie à la fois ; leave d'un contexte déjà quitté = no-op.
export const LeaveSessionEventSchema = z.object({
    type: z.literal("leave_session"),
});
export const LeaveGroupEventSchema = z.object({
    type: z.literal("leave_group"),
});
export const LeaveMatchEventSchema = z.object({
    type: z.literal("leave_match"),
});
export const TurnArchivedEventSchema = z.object({
    type: z.literal("turn_archived"),
    sessionId: z.string().min(1),
    turnId: z.string().min(1),
    authorId: z.string().min(1),
    textFinal: z.string(),
    startedAt: z.number(),
    endedAt: z.number(),
    readAt: z.number().nullable().optional(),
    deletedForEveryone: z.boolean().optional(),
    attachments: z.array(AttachmentSchema).optional(),
});
export const PresenceEventSchema = z.object({
    type: z.literal("presence"),
    sessionId: z.string().min(1),
    userId: z.string().min(1),
    status: z.enum(["joined", "left"]),
});
/**
 * Client→server: "my upload just started". Carries no userId — the server
 * fills it from the authenticated connection before fanning out, exactly as it
 * does for `delta` → `authorId`. Trusting a client-supplied userId here would
 * let anyone forge activity as anyone else.
 */
export const UploadingEventSchema = z.object({
    type: z.literal("uploading"),
    sessionId: z.string().min(1),
    kind: AttachmentKindSchema,
});
/**
 * Server→client counterpart of the above, so the other side sees
 * "Henri envoie une photo…" in the mirror zone — the media counterpart of
 * live typing. Deliberately carries no percentage: §1 forbids the fioriture.
 */
export const PeerUploadingEventSchema = z.object({
    type: z.literal("peer_uploading"),
    sessionId: z.string().min(1),
    userId: z.string().min(1),
    kind: AttachmentKindSchema,
});
export const ErrorEventSchema = z.object({
    type: z.literal("error"),
    code: z.string(),
    message: z.string(),
});
export const SessionAcceptedEventSchema = z.object({
    type: z.literal("session_accepted"),
    sessionId: z.string().min(1),
});
export const TurnReadEventSchema = z.object({
    type: z.literal("turn_read"),
    sessionId: z.string().min(1),
    turnId: z.string().min(1),
    readAt: z.number(),
});
export const ContactAddedEventSchema = z.object({
    type: z.literal("contact_added"),
    contact: z.any(),
});
export const ContactActivityEventSchema = z.object({
    type: z.literal("contact_activity"),
    contactUserId: z.string().min(1),
    lastActivityAt: z.string().min(1),
});
export const TurnDeletedEventSchema = z.object({
    type: z.literal("turn_deleted"),
    sessionId: z.string().min(1),
    turnId: z.string().min(1),
});
export const UnreadStatusChangedEventSchema = z.object({
    type: z.literal("unread_status_changed"),
    contactUserId: z.string().min(1),
    hasUnread: z.boolean(),
});
// ---------------------------------------------------------------------------
// Union of all WS messages
// ---------------------------------------------------------------------------
export const WsMessageSchema = z.discriminatedUnion("type", [
    HelloEventSchema,
    DeltaEventSchema,
    EndTurnEventSchema,
    PingEventSchema,
    PongEventSchema,
    ErrorEventSchema,
    JoinSessionEventSchema,
    TurnArchivedEventSchema,
    PresenceEventSchema,
    SessionAcceptedEventSchema,
    TurnReadEventSchema,
    TurnDeletedEventSchema,
    ContactAddedEventSchema,
    ContactActivityEventSchema,
    UnreadStatusChangedEventSchema,
    UploadingEventSchema,
    PeerUploadingEventSchema,
    GameEventSchema,
    GameMatchUpdatedEventSchema,
    GroupTurnArchivedEventSchema,
    GroupActivityEventSchema,
    PointsAwardedEventSchema,
]);
// ---------------------------------------------------------------------------
// Client→Server subset (what the server expects to receive)
// ---------------------------------------------------------------------------
export const ClientMessageSchema = z.discriminatedUnion("type", [
    DeltaEventSchema,
    EndTurnEventSchema,
    PingEventSchema,
    JoinSessionEventSchema,
    UploadingEventSchema,
    JoinMatchEventSchema,
    GameActionEventSchema,
    JoinGroupEventSchema,
    LeaveSessionEventSchema,
    LeaveGroupEventSchema,
    LeaveMatchEventSchema,
]);
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Parse a raw JSON string into a validated WsMessage.
 * Returns `{ ok: true, data }` or `{ ok: false, error }`.
 */
export function parseWsMessage(raw) {
    try {
        const json = JSON.parse(raw);
        const result = WsMessageSchema.safeParse(json);
        if (result.success) {
            return { ok: true, data: result.data };
        }
        return { ok: false, error: result.error };
    }
    catch {
        return {
            ok: false,
            error: new z.ZodError([
                {
                    code: z.ZodIssueCode.custom,
                    message: "Invalid JSON",
                    path: [],
                },
            ]),
        };
    }
}
/**
 * Parse a raw JSON string as a client message specifically.
 */
export function parseClientMessage(raw) {
    try {
        const json = JSON.parse(raw);
        const result = ClientMessageSchema.safeParse(json);
        if (result.success) {
            return { ok: true, data: result.data };
        }
        return { ok: false, error: result.error };
    }
    catch {
        return {
            ok: false,
            error: new z.ZodError([
                {
                    code: z.ZodIssueCode.custom,
                    message: "Invalid JSON",
                    path: [],
                },
            ]),
        };
    }
}
/**
 * Serialize a WsMessage to a JSON string.
 */
export function serializeMessage(msg) {
    return JSON.stringify(msg);
}
//# sourceMappingURL=events.js.map